<script setup>
  import { computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Button } from "@/components/ui/button";
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
  import { Badge } from "@/components/ui/badge";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import { Banner } from "@/components/episto-ui/banner";
  import { usePricerProjectStore } from "@/stores/pricer/pricerProject";
  import { usePricerHubSpotStore } from "@/stores/pricer/pricerHubSpot";
  import { useUsersStore } from "@stores/users";
  import { getFieldMappings } from "@/stores/pricer/pricerHubSpotMapping";

  const props = defineProps({
    open: { type: Boolean, required: true },
  });

  const emit = defineEmits(["update:open"]);

  const projectStore = usePricerProjectStore();
  const { project, hasProjectChanged } = storeToRefs(projectStore);

  const hubSpotStore = usePricerHubSpotStore();
  const { getHubSpotData, fetchHubSpotData, syncToHubSpot } = hubSpotStore;
  const { isSyncing, isLoading } = storeToRefs(hubSpotStore);

  const usersStore = useUsersStore();
  const { users } = storeToRefs(usersStore);

  watch(
    () => props.open,
    async (isOpen) => {
      if (isOpen && project.value?.token) {
        await fetchHubSpotData(project.value.token);
      }
    },
    { immediate: true },
  );

  const fieldMappings = computed(() => {
    const hubSpotData = getHubSpotData();
    const mappedFields = getFieldMappings().map((mapping) => {
      let getProjectValueFn = mapping.getProjectValue;
      let getDisplayValueFn = null;
      let getCurrentDisplayValueFn = null;

      if (mapping.key === "dealOwner" && !getProjectValueFn) {
        getProjectValueFn = (project) => {
          if (project.owner) {
            return project.owner.crm_id || "";
          }
          const user = users.value?.find((u) => u.id === project.owner_id);
          return user?.crm_id || "";
        };

        getDisplayValueFn = (project) => {
          if (project.owner) {
            return (
              project.owner.full_name ||
              `${project.owner.first_name || ""} ${project.owner.last_name || ""}`.trim() ||
              ""
            );
          }
          const user = users.value?.find((u) => u.id === project.owner_id);
          return user?.full_name || (user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "") || "";
        };

        getCurrentDisplayValueFn = () => {
          return hubSpotData.hs_owner_sales || hubSpotData.hs_owner_csm || hubSpotData.hs_owner_tm || "";
        };
      }

      const getCurrentValue = () => mapping.getHubSpotValue(hubSpotData);
      const getNewValue = () => getProjectValueFn(project.value);
      const getDisplayValue = getDisplayValueFn
        ? () => getDisplayValueFn(project.value)
        : () => getProjectValueFn(project.value);
      const getCurrentDisplayValue = getCurrentDisplayValueFn || (() => mapping.getHubSpotValue(hubSpotData));

      let isModified;
      if (mapping.key === "dealOwner" && getCurrentDisplayValueFn) {
        isModified = String(getCurrentDisplayValue()) !== String(getDisplayValue());
      } else if (mapping.key === "dealCompanies" || mapping.key === "dealContacts") {
        isModified = String(getCurrentValue()?.id) !== String(getNewValue()?.id);
      }
      else {
        isModified = String(getCurrentValue()) !== String(getNewValue());
      }

      return {
        ...mapping,
        getCurrentValue,
        getNewValue,
        getDisplayValue,
        getCurrentDisplayValue,
        isModified,
      };
    });

    return mappedFields.sort((a, b) => (b.isModified ? 1 : 0) - (a.isModified ? 1 : 0));
  });

  const changedFields = computed(() => {
    return fieldMappings.value
      .map((field) => {
        let currentValue, newValue, isChanged;

        if (field.key === "dealOwner" && field.getCurrentDisplayValue && field.getDisplayValue) {
          currentValue = field.getCurrentDisplayValue();
          newValue = field.getDisplayValue();
          isChanged = String(currentValue) !== String(newValue);
        } else if (field.key === "dealCompanies" || field.key === "dealContacts") {
          currentValue = field.getCurrentValue();
          newValue = field.getNewValue();
          isChanged = String(currentValue?.id) !== String(newValue?.id);
        } else {
          currentValue = field.getCurrentValue();
          newValue = field.getNewValue();
          isChanged = String(currentValue) !== String(newValue);
        }

        return {
          ...field,
          currentValue,
          newValue,
          isChanged,
        };
      })
      .filter((field) => field.isChanged);
  });

  const modifiedCount = computed(() => changedFields.value.length);

  const isNewDeal = computed(() => !project.value?.crm_id);

  const canSync = computed(() => {
    return modifiedCount.value > 0 && !isSyncing.value && !hasProjectChanged.value;
  });

  const handleConfirm = async () => {
    const result = await syncToHubSpot(project.value, changedFields.value);
    if (result.success) {
      emit("update:open", false);
    }
  };

  const formatFieldValue = (field, value) => {
    if (field?.formatValue) {
      return field.formatValue(value);
    }
    return value || "-";
  };
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)">
    <DialogContent
      class="sm:max-w-[70%] max-h-[90vh] flex flex-col"
      :show-close-button="false">
      <DialogHeader>
        <DialogTitle class="text-xl font-semibold">
          {{ isNewDeal ? "Create HubSpot Deal" : "HubSpot Synchronization" }}
        </DialogTitle>
        <p class="text-sm text-muted-foreground mt-1">
          {{ isNewDeal ? "A new deal will be created in HubSpot" : "Review changes before synchronizing" }}
        </p>
      </DialogHeader>

      <LoadingState
        v-if="isLoading"
        label="Loading HubSpot data..."
        class="flex-1 min-h-[400px]" />

      <template v-else>
        <Banner v-if="hasProjectChanged">
          <span class="font-semibold">Warning:</span>
          This project has unsaved modifications. You must save the project before syncing to HubSpot.
        </Banner>

        <Banner
          v-if="modifiedCount > 0"
          variant="info">
          <span class="font-semibold">{{ modifiedCount }} fields modified</span>
          - Highlighted values will be replaced on HubSpot
        </Banner>

        <div class="flex-1 overflow-hidden flex flex-col min-h-0">
          <div class="border rounded-md overflow-hidden">
            <div
              class="overflow-y-auto max-h-[400px] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-muted-foreground/50">
              <Table>
                <TableHeader class="sticky top-0 bg-background z-10">
                  <TableRow class="hover:bg-transparent border-b border-border">
                    <TableHead class="w-[50px]"></TableHead>
                    <TableHead class="text-sm font-medium text-muted-foreground"> Fields </TableHead>
                    <TableHead class="text-sm font-medium text-muted-foreground"> HubSpot (Current) </TableHead>
                    <TableHead class="text-sm font-medium text-muted-foreground"> HubSpot (New) </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow
                    v-for="field in fieldMappings"
                    :key="field.key"
                    class="hover:bg-muted/50 border-b border-border">
                    <TableCell class="w-[50px]">
                      <Badge
                        v-if="field.isModified"
                        variant="default"
                        class="bg-status-success hover:bg-status-success text-white border-0">
                        Modified
                      </Badge>
                    </TableCell>
                    <TableCell class="font-medium">{{ field.label }}</TableCell>
                    <TableCell class="text-muted-foreground">
                      {{
                        formatFieldValue(
                          field,
                          field.getCurrentDisplayValue ? field.getCurrentDisplayValue() : field.getCurrentValue(),
                        )
                      }}
                    </TableCell>
                    <TableCell>
                      <span
                        v-if="field.isModified"
                        class="font-semibold">
                        {{
                          formatFieldValue(field, field.getDisplayValue ? field.getDisplayValue() : field.getNewValue())
                        }}
                      </span>
                      <span
                        v-else
                        class="text-muted-foreground">
                        {{
                          formatFieldValue(field, field.getDisplayValue ? field.getDisplayValue() : field.getNewValue())
                        }}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </template>

      <div
        v-if="!isLoading"
        class="flex justify-between items-center pt-4 border-t border-border">
        <p
          v-if=" !isNewDeal"
          class="text-sm text-muted-foreground">
          <span class="font-semibold"> {{ modifiedCount > 0 ?`${modifiedCount} fields will be updated` : 'No changes detected'}} </span>
        </p>
        <p
          v-else
          class="text-sm text-muted-foreground">
          A new deal will be created
        </p>
        <div class="flex gap-2">
          <Button
            variant="outline"
            @click="emit('update:open', false)">
            Cancel
          </Button>
          <Button
            :disabled="!canSync"
            @click="handleConfirm">
            <span v-if="isSyncing">{{ isNewDeal ? "Creating..." : "Syncing..." }}</span>
            <span v-else>{{ isNewDeal ? "Create Deal" : "Confirm" }}</span>
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
